import { APP_ROOT, MANIFEST_SIDECAR_PATH } from '../../fsLayout';
import {
  MDX_METADATA_SIDECAR_PATH,
  type MdxMetadataRejection,
  parseMdxMetadataSidecar,
} from '@immediately-run/platform-constants';
import { parseFrontmatter, transformFile } from '@immediately-run/transpiler';
import {
  ARTIFACTS_DIR,
  type ArtifactIndex,
  type EmbeddedToolchainIdentity,
  manifestShaMap,
  normalizeRepoRelPath,
  outWithinArtifacts,
  parseArtifactIndex,
  readableLayerOnly,
  toolchainMismatch,
  type ToolchainStampMismatch,
  validateSeedEntry,
} from './artifactIndex';

const TRANSPILED_ROOT = '/transpiled';
// Repo-relative seeding-input paths (leading-slash, the dirty-set/COW key space).
const INDEX_REPO_PATH = `/${ARTIFACTS_DIR}/index.json`;
/** The frontmatter content-collection sidecar (MDX_CONTENT_COLLECTIONS_SPEC §1.3).
 *  The path and the file's SCHEMA are owned by platform-constants (R3-275) — the CLI
 *  writes what this reads, and they used to describe the format independently. */
const MDX_METADATA_REPO_PATH = `/${MDX_METADATA_SIDECAR_PATH}`;
/** Shared empty dirty set for roots the app's COW bookkeeping does not describe. */
const EMPTY_DIRTY_SET: ReadonlySet<string> = new Set<string>();
/** Minimum spot-verification sample size (§5.7 — K ≥ 2, a floor not a ceiling). */
export const SPOT_VERIFY_K = 2;

/** The narrow fs surface the store needs (CachedFS implements all of these). */
export interface ArtifactFs {
  readFileAsync(path: string): Promise<string>;
  writeFile(path: string, content: string): Promise<void>;
  isFileAsync(path: string): Promise<boolean>;
  deleteFile(path: string): Promise<void>;
}

/**
 * The `/transpiled` tmpfs path a module consults/writes through.
 *
 * Keyed by the module's FULL absolute path, not its repo-relative one. With a single
 * `/app` root the two were interchangeable; with library roots they are not — an app file
 * committed at `node_modules/x.tsx` and a mounted library's `x.tsx` both strip to the same
 * repo-relative key, and one would serve the other's bytes. `/app` and `/node_modules` are
 * distinct top-level mounts, so full paths cannot collide by construction.
 */
function transpiledPathFor(modulePath: string): string {
  return `${TRANSPILED_ROOT}${modulePath}.js`;
}

/** True for `path` inside `root` (the root itself counts). */
function isUnder(path: string, root: string): boolean {
  return path === root || path.startsWith(`${root}/`);
}

export interface SeedContext {
  /** §5.2 dirty set: repo-relative paths never seeded (writable-layer + deleted). */
  dirtySet: ReadonlySet<string>;
  /** Writable (COW) layer membership for the §5.1 readable-layer-only gate. */
  writableLayer: ReadonlySet<string>;
}

export interface SeedResult {
  seeded: number;
  /** Set when the whole section was rejected for a security reason (§5.1 PT2-4). */
  securityReject?: 'writable-layer-artifact';
  /** Per-root §4.4 stamp-gate refusals (R3-843): the zip carried a full payload and the
   *  runtime ignored ALL of it — the case that went dark until the reason rode the
   *  result and the bundler logged it. Absent/invalid indexes are NOT here (a repo
   *  with no artifacts is the ordinary case, not a mismatch). */
  stampMismatches?: { root: string; mismatch: ToolchainStampMismatch }[];
}

/** The result of a spot-verification pass (§5.7). */
export interface SpotVerifyResult {
  /** True if a sampled artifact ≠ `transpile(source)` — the section was distrusted. */
  tampered: boolean;
  /** The first mismatching module's app path (for the security event / log). */
  path?: string;
  /** How many modules were sampled (`min(K, |consumed|)`). */
  sampled: number;
  /** What kind of mismatch fired the distrust (§5.7 byte check vs §3 frontmatter). */
  reason?: 'transpile-mismatch' | 'frontmatter-mismatch';
}

/**
 * A sidecar entry the shared validator dropped, tagged with the root whose sidecar
 * carried it. The raw key is repo-relative, so once more than one root can seed
 * (a content mount alongside `/app`) the key alone no longer names a file.
 */
interface MdxMetadataDrop {
  root: string;
  path: string;
  reason: MdxMetadataRejection;
}

/**
 * What a seed produced across roots, with no verdict about `/app`. Every field but `entries`
 * is a reason something is MISSING from them — each contained to one root, and none of them
 * silent, because "the cache seeded nothing" and "there was nothing to seed" look the same
 * from the store.
 */
export interface MdxMetadataAdditive {
  /** Absolute root-joined path → frontmatter, for entries that pass confinement. */
  entries: Map<string, Record<string, any>>;
  /** Malformed ENTRIES the validator dropped from otherwise-usable sidecars. */
  droppedEntries: MdxMetadataDrop[];
  /** Non-app roots whose sidecar existed but could not be interpreted. */
  unusableRoots: { root: string; reason: MdxMetadataRejection }[];
  /**
   * Keys a NON-OWNING root declared frontmatter for. Root-joined keys are distinct whenever
   * the roots are, so this is reachable only through nested roots, which `addRoot` permits.
   * The owning root — `rootFor`, the innermost, the same rule `consult` attributes a module's
   * bytes by — keeps the key whatever order the roots registered in; every other root's entry
   * for it lands here rather than overwriting or being dropped silently. A key appears with no
   * winner when only a non-owning root described the file at all.
   */
  collisions: string[];
}

/** The confined result of reading the frontmatter sidecar (§1.3). */
export interface MdxMetadataSeedResult extends MdxMetadataAdditive {
  /** True if the APP root's sidecar file existed (whether or not any entry was honored). */
  present: boolean;
  /** Set if the sidecar itself sat in the writable (COW) layer → rejected (§3). */
  securityReject?: 'writable-layer-mdx-metadata';
  /**
   * Set when the sidecar EXISTS but this reader cannot interpret it — the shared
   * validator's file-level verdict (R3-275c).
   *
   * `present` alone cannot carry this: it answers "was there a file?", and the caller
   * needs "can I use it?". Collapsing the two is what made a corrupt sidecar seed an
   * empty store with no live walk behind it — a cache that returns a WRONG answer when
   * it is damaged instead of a slower one. The caller must live-scan on this, exactly
   * as it does on `securityReject`.
   */
  unusable?: MdxMetadataRejection;
}

/** The additive diagnostics, all empty — the shape a distrusted or no-op seed returns. */
const emptySeedDiagnostics = (): Omit<MdxMetadataAdditive, 'entries'> => ({
  droppedEntries: [],
  unusableRoots: [],
  collisions: [],
});

/** One root's sidecar verdict, before `seedMdxMetadata` aggregates them. */
interface MdxMetadataRootResult {
  entries: Map<string, Record<string, any>>;
  present: boolean;
  securityReject?: 'writable-layer-mdx-metadata';
  unusable?: MdxMetadataRejection;
  droppedEntries: MdxMetadataDrop[];
}

/**
 * Runtime consumption of pre-transpiled artifacts (PRETRANSPILED_ARTIFACTS_SPEC
 * §5.1 seeding, §5.3 consult / write-through / reset-delete). Owns the
 * `/transpiled` tmpfs and the per-module dependency records that a seeded
 * (never-live-transpiled) module needs — its deps live in the index, not in the
 * compiled bytes. All validation is delegated to the pure G2-2 core
 * (`artifactIndex.ts`); this class is the I/O wiring around it.
 */
export class ArtifactStore {
  // Raw dep specifiers per module app-path — for seeded modules (from the index)
  // and written-through modules (collected live), so a consult HIT can re-register
  // them without re-running the chain.
  private deps = new Map<string, string[]>();
  // R3-900: in-flight `/transpiled` ops per path — deletes AND writes on ONE
  // chain, each link never rejecting (a failed write must not wedge the path).
  // `invalidate` used to start an unlink that only `consult` awaited while
  // `writeThrough` wrote straight past it: two writers, or a write and an
  // in-flight unlink, reached one tmpfs path and ZenFS `commitNew` answered the
  // second create with EEXIST — a cache collision surfacing as a load failure.
  private pendingOps = new Map<string, Promise<void>>();
  // App paths actually seeded from the index (the universe spot-verify samples).
  private seededPaths = new Set<string>();
  // App paths a consult actually served from a seeded artifact (the consumed set).
  private consumed = new Set<string>();
  // Set once a spot-verify mismatch (or any §5.7 distrust) fires: the section is
  // discarded for the session — no further consults, no re-seed.
  private distrusted = false;
  // The cache zip's commit (from the sidecar) — reported with a distrust verdict.
  private commitSha: string | null = null;
  // Subtrees that may carry their own artifact section. `/app` (the app repo) is always
  // present; a git-mounted library adds its own via `addRoot`. Longest-prefix wins, so a
  // library nested under another root is attributed to the innermost one.
  private roots: string[] = [APP_ROOT];
  // Roots already seeded, so a second `seed()` (a post-boot library registration) only
  // does the new ones.
  private seededRoots = new Set<string>();
  // The same guard for the frontmatter sidecar. It is a SEPARATE set because the two seeds
  // run at different moments: the app root's metadata is read at boot (it decides whether the
  // tree is live-walked), while a root registered later is metadata-seeded alongside its
  // artifacts. Sharing one set would make whichever ran first silence the other.
  private seededMetadataRoots = new Set<string>();
  // Which root claimed each frontmatter key, across passes — so the later pass can tell a key
  // it is re-deriving from one another root already owns. Kept HERE rather than read off the
  // bundler's metadata store, which also holds keys from live reads that no root claimed.
  private metadataClaims = new Map<string, string>();
  // Artifact entries a root declared for a path a NESTED root owns, dropped rather than written
  // (see `seedRoot`). Reported so a repo whose two roots overlap can find out.
  private notOwnedSeeds: string[] = [];

  constructor(private readonly fs: ArtifactFs, private readonly embedded: EmbeddedToolchainIdentity) {}

  /**
   * Register a git-mounted library's subtree as an artifact root (LIBRARY_MOUNTS_SPEC §7 /
   * L4). The library repo's cache zip already carries its own pre-transpiled artifacts and
   * manifest sidecar; before this they rode along and were ignored, because every path this
   * store touched was anchored at `/app` — so a consumer re-transpiled the whole library on
   * every cold boot despite having the compiled bytes in hand.
   *
   * **Why the §5.1 readable-layer gate does not transfer, and what replaces it.** That gate
   * defends against an artifact planted in the app's COW writable layer surviving a clean
   * Refresh. A library's bytes are not app-writable: they arrive on a host-mediated
   * read-only mount and are copied in by the sandbox itself (`registerGitLibraryMount`),
   * which necessarily puts them on the writable side of `/node_modules` — so the gate would
   * be either vacuous or a blanket refusal, and neither is a security property.
   *
   * What DOES transfer is the check that actually binds artifacts to sources: every entry
   * must name a path in the library's own manifest sidecar and match its `srcSha`
   * (`validateSeedEntry`). A library fetched over REST rather than from its cache zip ships
   * no sidecar, so its manifest map is empty and EVERY entry is `not-in-manifest` — zero
   * seeded, live transpile, no special case. Artifacts are consumed only on the cache-zip
   * path, which is exactly the ref-based trust LIBRARY_MOUNTS_SPEC §11 already accepts:
   * the library runs with the app's own authority and the app already loads its own code
   * from GitHub under the same trust. §4.4's toolchain stamp and §5.7 spot-verification
   * apply per-root and unchanged.
   */
  addRoot(root: string): void {
    const normalized = root.replace(/\/+$/, '');
    if (!normalized.startsWith('/') || this.roots.includes(normalized)) return;
    this.roots.push(normalized);
  }

  /** How many artifact roots are registered (`/app` plus one per git-library mount).
   *  Reported alongside the seeded count so "seeded 0" can be told apart from "there was
   *  nothing to seed from" without reading the code. */
  rootCount(): number {
    return this.roots.length;
  }

  /** The artifact root a module belongs to (longest prefix), or null when it is in none. */
  private rootFor(modulePath: string): string | null {
    let best: string | null = null;
    for (const root of this.roots) {
      if (isUnder(modulePath, root) && (best === null || root.length > best.length)) best = root;
    }
    return best;
  }

  /**
   * Seed `/transpiled` from each root's `.immediately.run/artifacts/index.json` (§5.1). Returns
   * the count seeded; a no-op (0) when the index is absent/invalid, the toolchain
   * stamp mismatches, or the readable-layer-only gate fails. Per-file failures
   * (dirty, srcSha mismatch, bad path, …) skip just that file (§5.5).
   */
  async seed(ctx: SeedContext): Promise<SeedResult> {
    // The parent's §5.7 distrust mark for this (coords, commitSha) pre-distrusts
    // the section → seed nothing (the served boot treats artifacts as absent).
    if (this.distrusted) return { seeded: 0 };

    let seeded = 0;
    let securityReject: SeedResult['securityReject'];
    let stampMismatches: SeedResult['stampMismatches'];
    for (const root of this.roots) {
      if (this.seededRoots.has(root)) continue;
      this.seededRoots.add(root);
      const result = await this.seedRoot(root, ctx);
      seeded += result.seeded;
      // A rejection anywhere is worth surfacing; the app root's is the one that can
      // actually fire (see `addRoot` on why the gate is app-scoped), so first wins.
      securityReject ??= result.securityReject;
      if (result.stampMismatches) stampMismatches = [...(stampMismatches ?? []), ...result.stampMismatches];
    }
    return {
      seeded,
      ...(securityReject ? { securityReject } : {}),
      ...(stampMismatches ? { stampMismatches } : {}),
    };
  }

  /** Seed one root's artifact section. Every failure mode is a no-op for THAT root only —
   *  a library with a stale or absent section must never cost the app its artifacts. */
  private async seedRoot(root: string, ctx: SeedContext): Promise<SeedResult> {
    const isAppRoot = root === APP_ROOT;
    let index: ArtifactIndex | null = null;
    try {
      index = parseArtifactIndex(JSON.parse(await this.fs.readFileAsync(`${root}${INDEX_REPO_PATH}`)));
    } catch {
      return { seeded: 0 };
    }
    if (!index) return { seeded: 0 };
    // §4.4 stamp gate: both version and toolchainHash must match. Per-root — a library
    // built with an older toolchain live-transpiles while the app still uses its artifacts.
    const mismatch = toolchainMismatch(index.toolchain, this.embedded);
    if (mismatch) return { seeded: 0, stampMismatches: [{ root, mismatch }] };

    // §5.1 readable-layer-only (PT2-4): the index, the sidecar, and EVERY artifact
    // file must live in the readable (zip) layer only; any in the writable layer
    // rejects the WHOLE section (a planted artifact could survive a clean Refresh).
    // App root only — `ctx.writableLayer` is the APP's COW dirty set, keyed by
    // repo-relative path, so applying it to a library's paths would compare two
    // different key spaces. See `addRoot` for what defends a library root instead.
    if (isAppRoot) {
      const seedingInputs = [INDEX_REPO_PATH, MANIFEST_SIDECAR_PATH];
      for (const entry of Object.values(index.files)) {
        // `entry.out` is relative to `.immediately.run/artifacts/`; confine it (a `..`
        // entry can't reach a writable-layer file outside the dir) then take its
        // repo-relative form `/<confined>`.
        const confined = outWithinArtifacts(entry.out);
        if (confined) seedingInputs.push(`/${confined}`);
      }
      if (!readableLayerOnly(seedingInputs, ctx.writableLayer)) {
        return { seeded: 0, securityReject: 'writable-layer-artifact' };
      }
    }

    const manifestShas = await this.readManifestShas(root);
    // The dirty set is the app's COW bookkeeping, in repo-relative keys. A library mount is
    // not app-editable, so nothing in it can be dirty — and passing the app's set would
    // silently skip a library file that happens to share a repo-relative path with a file
    // the user edited in their own repo.
    const dirtySet = isAppRoot ? ctx.dirtySet : EMPTY_DIRTY_SET;
    let seeded = 0;
    const notOwned: string[] = [];
    for (const [rawKey, entry] of Object.entries(index.files)) {
      const v = validateSeedEntry(rawKey, entry, { manifestShas, dirtySet });
      if (!v.ok) continue;
      let content: string;
      try {
        // v.out is the confined `.immediately.run/artifacts/...` path; read it from the root.
        content = await this.fs.readFileAsync(`${root}/${v.out}`);
      } catch {
        continue;
      }
      const modulePath = `${root}${v.path}`;
      // Only the root that OWNS this path may seed it. Roots may nest (`addRoot` permits it),
      // and `/mnt/w` + `/sub/x.ts` and `/mnt/w/sub` + `/x.ts` are the same module — so without
      // this the two write the same `/transpiled` entry and the later root silently serves the
      // earlier one's bytes. `rootFor` is already the rule `consult` attributes by, so using it
      // here is what makes the two agree, and makes the outcome independent of registration
      // order. With no nesting it is always true and nothing changes.
      if (this.rootFor(modulePath) !== root) {
        notOwned.push(modulePath);
        continue;
      }
      // Same per-path chain as writeThrough/invalidate (R3-900) — a seed that
      // races a discard's reset-delete must not collide on the tmpfs slot.
      // And the §5.5 per-file rule applies to the write too: a rejected write
      // skips just this file (the cache fails soft; the module transpiles live).
      try {
        await this.runSequenced(transpiledPathFor(modulePath), () =>
          this.fs.writeFile(transpiledPathFor(modulePath), content),
        );
      } catch {
        continue;
      }
      this.deps.set(modulePath, v.deps);
      this.seededPaths.add(modulePath);
      seeded++;
    }
    if (notOwned.length) {
      this.notOwnedSeeds.push(...notOwned);
    }
    return { seeded };
  }

  /**
   * R3-900 — run `op` on `transpiledPath` AFTER every op already queued for it.
   * The returned promise carries the op's own outcome (a writeThrough caller sees
   * a rejected write and fails soft); the stored chain link never rejects, so one
   * failed op cannot wedge the path for the next. Serialisation is what makes a
   * write-after-delete and a write-after-write settle instead of racing ZenFS
   * `commitNew` into EEXIST.
   */
  private runSequenced(transpiledPath: string, op: () => Promise<void>): Promise<void> {
    const tail = this.pendingOps.get(transpiledPath) ?? Promise.resolve();
    const run = tail.then(op);
    const settled = run.catch(() => undefined);
    this.pendingOps.set(transpiledPath, settled);
    void settled.finally(() => {
      if (this.pendingOps.get(transpiledPath) === settled) this.pendingOps.delete(transpiledPath);
    });
    return run;
  }

  /**
   * Consult `/transpiled` for an app module (§5.3). HIT → the precompiled bytes +
   * its recorded deps; MISS → null (caller live-transpiles). Awaits any pending op
   * for this path — a reset-delete OR an in-flight write-through (R3-900) — so a
   * re-transform never reads a stale entry and never reads bytes mid-rewrite.
   */
  async consult(appModulePath: string): Promise<{ content: string; deps: string[] } | null> {
    if (this.distrusted || this.rootFor(appModulePath) === null) return null;
    const transpiledPath = transpiledPathFor(appModulePath);
    const pending = this.pendingOps.get(transpiledPath);
    if (pending) await pending;
    if (!(await this.fs.isFileAsync(transpiledPath))) return null;
    const content = await this.fs.readFileAsync(transpiledPath);
    // Genuinely-served seeded artifacts are the spot-verify universe (§5.7); a
    // written-through module is self-produced and can't be tampered.
    if (this.seededPaths.has(appModulePath)) this.consumed.add(appModulePath);
    return { content, deps: this.deps.get(appModulePath) ?? [] };
  }

  /**
   * Write a live-transpiled module through to `/transpiled` (§5.3 miss path), so an
   * in-session re-read (e.g. after an HMR invalidation cascade that didn't reset
   * THIS module) hits the tmpfs instead of the chain.
   */
  async writeThrough(appModulePath: string, compiled: string, deps: string[]): Promise<void> {
    if (this.rootFor(appModulePath) === null) return;
    // R3-900: serialised with any in-flight delete or earlier write of this path.
    // A rejection here is a missed cache entry — the caller keeps the compiled
    // module it already holds and logs; it must NOT throw the compile away.
    await this.runSequenced(transpiledPathFor(appModulePath), () =>
      this.fs.writeFile(transpiledPathFor(appModulePath), compiled),
    );
    this.deps.set(appModulePath, deps);
  }

  /**
   * Drop an app module's `/transpiled` entry (§5.3 reset-delete). Synchronous to
   * call (resetCompilation is sync); the async unlink is tracked so a queued
   * re-transform's `consult` awaits it before reading. A no-op for non-app paths.
   */
  invalidate(appModulePath: string): void {
    if (this.rootFor(appModulePath) === null) return;
    const transpiledPath = transpiledPathFor(appModulePath);
    this.deps.delete(appModulePath);
    // R3-900: the unlink rides the same per-path chain the writes do, so a
    // recompile's writeThrough queued behind it writes into an empty slot
    // instead of racing the unlink (ZenFS `commitNew` → EEXIST).
    void this.runSequenced(transpiledPath, () => this.fs.deleteFile(transpiledPath)).catch(() => undefined);
  }

  /** The `path → blob sha` map from ONE root's manifest sidecar. Absent (a REST-fetched
   *  library, or a repo loaded without its cache zip) → an empty map, which makes every
   *  artifact entry `not-in-manifest` and seeds nothing. */
  private async readManifestShas(root: string): Promise<Map<string, string>> {
    try {
      const raw = await this.fs.readFileAsync(`${root}${MANIFEST_SIDECAR_PATH}`);
      const sidecar = JSON.parse(raw) as {
        entries?: Array<{ path: string; sha: string }>;
        commitSha?: string;
      };
      // Capture the APP zip's commit so a distrust verdict can name it (§5.7); the parent
      // attributes the mark to the mount it owns, which is the app repo — a library's
      // commit must not overwrite it.
      if (root === APP_ROOT && typeof sidecar.commitSha === 'string') this.commitSha = sidecar.commitSha;
      return manifestShaMap(sidecar.entries ?? []);
    } catch {
      return new Map();
    }
  }

  /** The cache zip's commit, read from the sidecar during seeding (§5.7 key). */
  getCommitSha(): string | null {
    return this.commitSha;
  }

  /**
   * Read + confine the frontmatter sidecar (MDX_CONTENT_COLLECTIONS_SPEC §1.3) of EVERY
   * artifact root, so a clean cached boot seeds the metadata store from JSON instead of
   * walking the tree — and so a dispatched content mount's own sidecar seeds it too
   * (MDX_FROM_MOUNT_SPEC §3). The sidecar's keys are repo-relative internally, so seeding
   * from a mount is a rebase: join to the ACTIVE root rather than to `/app`.
   *
   * Aggregated the way `seed()` aggregates `seedRoot()`. `entries` is the union across roots,
   * but `present` / `securityReject` / `unusable` are the APP root's verdict ALONE, because
   * all three answer one question for the caller — *must I live-walk the app tree?* — which
   * only the app root's sidecar can answer. A mount's absent or damaged sidecar is contained
   * to that mount (surfaced in `unusableRoots`), never charged to `/app`.
   *
   * **Call this once**, at boot. It is the pass that carries the app root's verdict, and a
   * second call could not: `/app` has already been read, so `present` would come back `false`
   * meaning "not asked again", which the caller cannot tell from "there was no sidecar" and
   * would answer by walking the whole tree. Roots registered later are
   * {@link seedNewMetadataRoots}', which carries no verdict because it needs none.
   */
  async seedMdxMetadata(ctx: SeedContext): Promise<MdxMetadataSeedResult> {
    if (this.distrusted) return { entries: new Map(), present: false, ...emptySeedDiagnostics() };

    // The app root is read first and named explicitly rather than found in the loop: its
    // verdict is the returned one, so making that structural removes the "what if `/app`
    // is not in `roots`" branch a lookup would need.
    const app = await this.seedMdxMetadataRootOnce(APP_ROOT, ctx);
    const claimed = this.claimEntries(APP_ROOT, app.entries);
    const rest = await this.seedNewMetadataRoots(ctx);
    for (const [modulePath, frontmatter] of claimed.entries) rest.entries.set(modulePath, frontmatter);
    return {
      ...rest,
      present: app.present,
      ...(app.securityReject ? { securityReject: app.securityReject } : {}),
      ...(app.unusable ? { unusable: app.unusable } : {}),
      droppedEntries: [...app.droppedEntries, ...rest.droppedEntries],
      collisions: [...claimed.collisions, ...rest.collisions],
    };
  }

  /**
   * The additive half: every root registered since the last seed, and nothing about `/app`.
   * Re-callable — `seededMetadataRoots` means each root is read once, however often this runs.
   */
  async seedNewMetadataRoots(ctx: SeedContext): Promise<MdxMetadataAdditive> {
    if (this.distrusted) return { entries: new Map(), ...emptySeedDiagnostics() };
    const entries = new Map<string, Record<string, any>>();
    const droppedEntries: MdxMetadataDrop[] = [];
    const unusableRoots: { root: string; reason: MdxMetadataRejection }[] = [];
    const collisions: string[] = [];

    for (const root of this.roots) {
      if (root === APP_ROOT) continue;
      const result = await this.seedMdxMetadataRootOnce(root, ctx);
      const claimed = this.claimEntries(root, result.entries);
      for (const [modulePath, frontmatter] of claimed.entries) entries.set(modulePath, frontmatter);
      collisions.push(...claimed.collisions);
      droppedEntries.push(...result.droppedEntries);
      if (result.unusable) unusableRoots.push({ root, reason: result.unusable });
    }
    return { entries, droppedEntries, unusableRoots, collisions };
  }

  /**
   * Record which root a frontmatter key came from, keeping only the entries `root` OWNS.
   *
   * Two roots produce one key only when one NESTS inside the other, which `addRoot` permits.
   * `rootFor` — the innermost — is already the rule `consult` attributes a module's BYTES by,
   * so it decides the frontmatter too: otherwise one file's compiled output and its metadata
   * would be attributed to different roots, and the answer would depend on registration order.
   * The entry that loses is returned in `collisions`, never dropped silently.
   *
   * Ownership is re-arbitrated on EVERY pass, not settled by whoever claimed first. A root
   * can stop being a key's owner between passes — register `/mnt/w`, seed it, then register
   * `/mnt/w/sub` — and `seed()` moves the BYTES to the new owner when that happens, so a
   * claim that outranked ownership would leave the two halves split, which is the exact thing
   * this rule exists to prevent.
   */
  private claimEntries(
    root: string,
    entries: ReadonlyMap<string, Record<string, any>>,
  ): { entries: Map<string, Record<string, any>>; collisions: string[] } {
    const kept = new Map<string, Record<string, any>>();
    const collisions: string[] = [];
    for (const [modulePath, frontmatter] of entries) {
      if (this.rootFor(modulePath) !== root) {
        collisions.push(modulePath);
        continue;
      }
      // The owner takes the key over from an earlier, now-non-owning claimant; that root's
      // entry is the one displaced, so it is what `collisions` names.
      const displaced = this.metadataClaims.get(modulePath);
      if (displaced !== undefined && displaced !== root) collisions.push(modulePath);
      this.metadataClaims.set(modulePath, root);
      kept.set(modulePath, frontmatter);
    }
    return { entries: kept, collisions };
  }

  /** `seedMdxMetadataRoot`, but at most once per root for the life of the store — so a
   *  second seed does only the roots that registered since the first. */
  private async seedMdxMetadataRootOnce(root: string, ctx: SeedContext): Promise<MdxMetadataRootResult> {
    if (this.seededMetadataRoots.has(root)) {
      return { entries: new Map(), present: false, droppedEntries: [] };
    }
    this.seededMetadataRoots.add(root);
    return this.seedMdxMetadataRoot(root, ctx);
  }

  /**
   * One root's frontmatter sidecar. `present:false` when the sidecar is absent → that
   * root contributes nothing and its files are live-read. A writable-layer sidecar is
   * rejected eagerly (§3). Each entry is honored only if it passes the SAME confinement
   * as `§5.1` artifact seeding: a valid repo-relative path that names a manifest
   * `entries[]` member, is not in the dirty set, and whose `srcSha` equals the manifest
   * sha. Keys are joined to `root` for the metadata-store space (`metadataKey.test.ts`).
   */
  private async seedMdxMetadataRoot(root: string, ctx: SeedContext): Promise<MdxMetadataRootResult> {
    const isAppRoot = root === APP_ROOT;
    const absent: MdxMetadataRootResult = { entries: new Map(), present: false, droppedEntries: [] };

    let raw: string;
    try {
      raw = await this.fs.readFileAsync(`${root}${MDX_METADATA_REPO_PATH}`);
    } catch {
      return absent; // sidecar not shipped → live scan (§5.5)
    }
    // §3 readable-layer-only: a writable-layer (COW) sidecar could survive a clean
    // Refresh — reject the section and let the caller distrust + live-scan. App root
    // only: `ctx.writableLayer` is the APP's COW set in repo-relative keys, so testing a
    // mount's sidecar against it compares two different key spaces — the same reason
    // `seedRoot` scopes its readable-layer gate to `/app`.
    if (isAppRoot && ctx.writableLayer.has(MDX_METADATA_REPO_PATH)) {
      return {
        entries: new Map(),
        present: true,
        securityReject: 'writable-layer-mdx-metadata',
        droppedEntries: [],
      };
    }
    // SHAPE is the shared validator's call (R3-275): unparseable JSON, a schema
    // version this reader does not know, a non-object `files`, and every malformed
    // entry — one definition, the same verdicts the CLI's emitter is written against.
    const validation = parseMdxMetadataSidecar(raw);
    if (!validation.ok) {
      // Unusable, not absent, and not a security event: report the verdict and let the
      // caller fall back to the live walk (R3-275c). The sidecar is an OPTIMIZATION over
      // that walk; when it is damaged the honest outcome is the slow answer, never no
      // answer.
      return { entries: new Map(), present: true, unusable: validation.reason, droppedEntries: [] };
    }

    // CONFINEMENT stays here: it needs the manifest, the dirty set and the writable
    // layer, none of which the package can see. The validator answers "is this the
    // format?"; this answers "may I trust this entry?".
    const manifestShas = await this.readManifestShas(root);
    // The dirty set is the app's COW bookkeeping, in repo-relative keys — a mount is not
    // app-editable, and passing the app's set would silently skip a mount file that
    // happens to share a repo-relative path with one the user edited in their own repo.
    const dirtySet = isAppRoot ? ctx.dirtySet : EMPTY_DIRTY_SET;
    const entries = new Map<string, Record<string, any>>();
    for (const [rawKey, entry] of Object.entries(validation.sidecar.files)) {
      const path = normalizeRepoRelPath(rawKey);
      if (!path) continue; // bad/traversing key
      const manifestSha = manifestShas.get(path);
      if (manifestSha === undefined) continue; // not a committed member
      if (dirtySet.has(path)) continue; // modified → live fallback
      if (entry.srcSha !== manifestSha) continue; // source bytes drifted
      entries.set(`${root}${path}`, entry.frontmatter as Record<string, any>);
    }
    return {
      entries,
      present: true,
      droppedEntries: validation.rejected.map((r) => ({ root, path: r.path, reason: r.reason })),
    };
  }

  /**
   * Pre-distrust the section before boot — the parent sets this from its persisted
   * §5.7 mark for this `(repo coordinates, commitSha)`, so `seed()` no-ops and the
   * boot treats the artifacts as absent until a new commit clears the mark.
   */
  markDistrusted(): void {
    this.distrusted = true;
  }

  /** Artifact entries dropped because a nested root owns the path (`seedRoot`). Drained by
   *  the caller so it is reported once, not on every later read. */
  takeNotOwnedSeeds(): string[] {
    const out = this.notOwnedSeeds;
    this.notOwnedSeeds = [];
    return out;
  }

  /** Every module path seeded from an index this session. The bundler needs this to ADOPT
   *  modules that were registered (as uncompiled) before seeding ran — a git-library's
   *  files are written and registered at mount-registration time, which is before the
   *  seed pass, so nothing would ever take the consult path for them. */
  seededPathList(): string[] {
    return [...this.seededPaths];
  }

  /** Number of seeded artifacts actually consulted this session (the §5.7 universe). */
  consumedCount(): number {
    return this.consumed.size;
  }

  /**
   * Mandatory spot-verification (§5.7): re-transpile up to `min(K, |consumed|)`
   * uniformly-random consumed modules through the embedded chain and byte-compare
   * each against the artifact it consumed. ANY mismatch → discard the WHOLE section
   * for the session (`discardAll`) and report tampered. The caller (idle-scheduled)
   * emits the §8.14 security event + the `artifact-distrust` host message.
   */
  async spotVerify(
    k: number = SPOT_VERIFY_K,
    opts?: {
      /** The seeded frontmatter for an app path (§3 extension), or undefined if none. */
      frontmatterOf?: (appModulePath: string) => Record<string, any> | undefined;
    },
  ): Promise<SpotVerifyResult> {
    const universe = [...this.consumed];
    const sample = sampleWithoutReplacement(universe, Math.min(k, universe.length));
    for (const appModulePath of sample) {
      let source: string;
      let consumedArtifact: string;
      try {
        source = await this.fs.readFileAsync(appModulePath);
        consumedArtifact = await this.fs.readFileAsync(transpiledPathFor(appModulePath));
      } catch {
        // The module was reset/evicted since it was consumed — it's live now, not
        // artifact-served; nothing to verify.
        continue;
      }
      const result = await transformFile({ path: appModulePath, code: source });
      if ('error' in result || result.code !== consumedArtifact) {
        this.discardAll();
        return { tampered: true, path: appModulePath, sampled: sample.length, reason: 'transpile-mismatch' };
      }
      // §3 frontmatter extension: `srcSha` binds the source blob, NOT the derived
      // frontmatter the sidecar seeded. Re-derive it from the re-read source (this
      // is the ONLY runtime path that re-reads a covered `.mdx` — verify-on-read is
      // dead on the consult-HIT path) and deep-compare; a mismatch is a forged/divergent
      // sidecar → distrust the section. Same source bytes → same parser output, so no
      // false positive for a benign publisher (the CLI uses this same parser, §1.3).
      const seededFrontmatter = opts?.frontmatterOf?.(appModulePath);
      if (seededFrontmatter !== undefined && appModulePath.endsWith('.mdx')) {
        const liveFrontmatter = parseFrontmatter(source).data;
        if (JSON.stringify(liveFrontmatter) !== JSON.stringify(seededFrontmatter)) {
          this.discardAll();
          return { tampered: true, path: appModulePath, sampled: sample.length, reason: 'frontmatter-mismatch' };
        }
      }
    }
    return { tampered: false, sampled: sample.length };
  }

  /**
   * Discard the whole artifact section for the session (§5.7): delete every
   * consumed `/transpiled` entry and mark distrusted so nothing is re-consumed
   * (consult returns null → live transpile). Returns the discarded app paths so the
   * bundler can re-transpile them live this session.
   */
  discardAll(): string[] {
    const discarded = [...this.consumed];
    this.distrusted = true;
    for (const appModulePath of discarded) this.invalidate(appModulePath);
    this.seededPaths.clear();
    this.consumed.clear();
    return discarded;
  }
}

// Fisher-Yates partial shuffle → a uniform sample of `n` distinct elements.
function sampleWithoutReplacement<T>(items: readonly T[], n: number): T[] {
  const copy = items.slice();
  for (let i = 0; i < n && i < copy.length; i++) {
    const j = i + Math.floor(Math.random() * (copy.length - i));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}
